import React, { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
      toast.success('Password updated — signing you in was skipped for security. Please log in.');
      setTimeout(() => nav('/login'), 2500);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Reset failed'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-full border border-[#d4a94a] flex items-center justify-center">
            <span className="font-display text-[#d4a94a] text-xl font-semibold leading-none">R</span>
          </div>
          <span className="font-display text-2xl text-zinc-100">Reachly</span>
        </Link>
        <Card className="bg-[#111110] border-zinc-900 p-8" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-950/40 border border-red-900 flex items-center justify-center text-red-300 mb-4">
                <AlertTriangle size={22}/>
              </div>
              <h1 className="font-display text-2xl text-zinc-100">Missing token.</h1>
              <p className="text-sm text-zinc-500 mt-2">This link doesn't have a reset token attached. Request a new one below.</p>
              <Link to="/forgot-password"><Button className="mt-6 bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium">Request new link</Button></Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a] mb-4">
                <CheckCircle2 size={22}/>
              </div>
              <h1 className="font-display text-2xl text-zinc-100">Password updated.</h1>
              <p className="text-sm text-zinc-400 mt-2">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl text-zinc-100">Set a new password.</h1>
              <p className="text-sm text-zinc-500 mt-1">Minimum 8 characters. This will sign you out of all other devices.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-zinc-300">New password</Label>
                  <Input type="password" required autoFocus minLength={8} value={pw} onChange={e=>setPw(e.target.value)} className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]" data-testid="reset-pw1"/>
                </div>
                <div>
                  <Label className="text-zinc-300">Confirm password</Label>
                  <Input type="password" required minLength={8} value={pw2} onChange={e=>setPw2(e.target.value)} className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]" data-testid="reset-pw2"/>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" data-testid="reset-submit-btn">
                  {loading ? 'Updating…' : 'Update password'}
                </Button>
              </form>
              <div className="mt-6 text-sm text-zinc-500 text-center">
                <Link to="/login" className="text-[#f5d67a] hover:underline">Back to sign in</Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
